import { useMemo, useState } from 'react';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { bills } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  Modal,
  Money,
} from '@/components/ui';
import { required, str, uuid } from '@/utils/format';
import { mediaUrl } from '@/utils/mediaUrl';

function firstParamName(product: JsonMap): string {
  const raw = product.params ?? product.params_json ?? product.fields;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (typeof first === 'string' && first.trim() !== '') return 'field_1';
    if (first && typeof first === 'object') {
      const name = str((first as JsonMap).name, 'field_1');
      return name.replace(/[^a-zA-Z0-9_]/g, '') || 'field_1';
    }
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === 'object') {
        const name = str((parsed[0] as JsonMap).name, 'field_1');
        return name.replace(/[^a-zA-Z0-9_]/g, '') || 'field_1';
      }
    } catch {
      /* ignore */
    }
  }
  return 'field_1';
}

export function BillsScreen() {
  const state = useAsync(
    () => bills.content(),
    (p) => ({
      categories: pickList(p, 'categories', 'items'),
      products: pickList(p, 'products', 'items', 'data'),
    }),
  );

  const [path, setPath] = useState<number[]>([0]);
  const [selected, setSelected] = useState<JsonMap | null>(null);
  const [account, setAccount] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const currentParent = path[path.length - 1] ?? 0;

  const view = useMemo(() => {
    const categories = (state.data?.categories ?? []).filter(
      (c) => Number(c.parent_id ?? c.parent ?? 0) === Number(currentParent),
    );
    const products = (state.data?.products ?? []).filter((p) => {
      const cat = Number(p.category_id ?? p.category ?? 0);
      return cat === Number(currentParent) || (currentParent === 0 && !p.category_id && !p.category);
    });
    // If root has no nested structure, show all products when no categories
    if (currentParent === 0 && categories.length === 0) {
      return { categories, products: state.data?.products ?? [] };
    }
    return { categories, products };
  }, [state.data, currentParent]);

  async function order() {
    if (!selected) return;
    const miss = required({ account }, ['account']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const field = firstParamName(selected);
    const res = await bills.order({
      product_id: selected.id ?? selected.product_id,
      order_uuid: uuid(),
      params: { [field]: account },
      extra: { [field]: account },
    });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setMsg(typeof res.message === 'string' ? res.message : 'تم إرسال طلب الفاتورة.');
    setAccount('');
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <div className="stack-gap bills-market">
      <SectionCard
        title="الفواتير"
        subtitle="خدمات تتم معالجتها من الإدارة، ويُخصم سعرها من محفظة الوحدات."
        icon={<Icon name="bill" size={24} />}
      >
        <div className="bills-breadcrumb">
          <button type="button" onClick={() => setPath([0])}>
            الرئيسية
          </button>
          {path.slice(1).map((id, idx) => (
            <button key={id} type="button" onClick={() => setPath(path.slice(0, idx + 2))}>
              قسم #{id}
            </button>
          ))}
          {path.length > 1 ? (
            <button type="button" className="btn btn-secondary" onClick={() => setPath(path.slice(0, -1))}>
              رجوع
            </button>
          ) : null}
        </div>

        <div className="bill-category-grid">
          {view.categories.map((c) => {
            const img = mediaUrl(c.image_url ?? c.image);
            return (
              <button
                key={str(c.id)}
                type="button"
                className="bill-category-card"
                onClick={() => setPath([...path, Number(c.id)])}
              >
                {img ? <img src={img} alt="" /> : <span><Icon name="orders" size={30} /></span>}
                <strong>{str(c.name)}</strong>
              </button>
            );
          })}
        </div>

        <div className="bill-product-grid">
          {view.products.map((p) => {
            const img = mediaUrl(p.image_url ?? p.image);
            return (
              <article key={str(p.id ?? p.product_id)} className="bill-product-card">
                {img ? (
                  <img src={img} alt="" />
                ) : (
                  <div className="bill-product-placeholder">
                    <Icon name="bill" size={34} />
                  </div>
                )}
                <div>
                  <small>{str(p.category_name, 'فواتير')}</small>
                  <h3>{str(p.name ?? p.title)}</h3>
                  {p.description ? <p>{str(p.description)}</p> : null}
                  <strong>
                    <Money value={p.price_syp ?? p.price ?? p.sale_price} />
                  </strong>
                  <Button type="button" onClick={() => setSelected(p)}>
                    طلب الخدمة
                  </Button>
                </div>
              </article>
            );
          })}
        </div>

        {!view.categories.length && !view.products.length ? (
          <Empty title="لا توجد منتجات بهذا القسم" hint="جرّب قسماً آخر أو راجع لاحقاً." />
        ) : null}
      </SectionCard>

      <Modal open={!!selected} title={str(selected?.name, 'دفع فاتورة')} onClose={() => setSelected(null)}>
        <Form className="form-grid" onSubmit={() => void order()}>
          <Input
            label="رقم الحساب / الاشتراك"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            required
          />
          {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
          <Button type="submit" disabled={busy}>
            {busy ? 'جاري الطلب…' : 'تأكيد الدفع'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
